/* eslint-disable react/no-multi-comp, react/prop-types */

// Modules
import React from 'react';
import moment from 'moment';
import numeral from 'numeral';
import _ from 'lodash';
import classNames from 'classnames';

// Components
import SVG from 'components/widgets/icons/svg';
import { CheckboxInput } from 'components/forms/form-elements';
import Tooltip from 'components/widgets/overlays/tooltip';
import TicketLink from 'components/models/ticket/ticket-link';
import EditTimeReportPopup from 'components/models/time-report/forms/edit-time-report-popup';
import EditExpenseBillPopup from 'components/models/purchases/forms/edit-expense-bill-popup';
import EditBillingPopup from 'components/models/agreement/edit-billing-popup';
import UserInitials from 'components/models/user/user-initials';
import AgreementMismatch from 'components/models/agreement/agreement-mismatch';
import AgreementLink from '../../../components/models/agreement/agreement-link';


// Components
export class TimeReportRow extends React.Component {
  shouldComponentUpdate(nextProps) {
    return (nextProps.timeReport.checked !== this.props.timeReport.checked) ||
      (nextProps.timeReport.updatedAt !== this.props.timeReport.updatedAt);
  }

  render() {
    const { timeReport, toggleChecked, refreshData } = this.props;
    const disabled = timeReport.checkboxDisabled;

    return (
      <Tooltip elementType="tr" title={disabled} className={classNames({ 'disabled-tbody': disabled })} onClick={(e) => toggleChecked(e, timeReport)}>
        {/* Checkbox */}
        <td className="checkbox-cell">
          { !disabled && <CheckboxInput input={{ value: timeReport.checked, onChange: _.noop }} /> }
        </td>

        <td className="icon-cell">
          <Tooltip title="Time Report">
            <SVG src="svg/time-report.svg" className="time-report" />
          </Tooltip>
        </td>

        {/* Duration */}
        <td className="right-align deemphasize">
          {timeReport.duration}
        </td>
        {/* Rate */}
        <td className="right-align deemphasize">
          {numeral(timeReport.cachedRate).format('$0,0.00')}
          { timeReport.afterHours &&
            <Tooltip
              title={`(${numeral(timeReport.rate.rate).format('$0,0.00')} +
                ${numeral(timeReport.afterHoursPremium).format('$0,0.00')} After Hours)`}
              elementType="span"
            >
              <br />
              <SVG src="svg/moon.svg" className="after-hours" />
            </Tooltip>
          }
        </td>
        {/* Amount */}
        <td className="right-align emphasize">
          {numeral(timeReport.amount).format('$0,0.00')}
        </td>
        {/* Date */}
        <td className="deemphasize">
          {moment(timeReport.date).format('M/D')}
        </td>
        {/* Agreement */}
        <td className="deemphasize">
          {timeReport.agreement.nickname}
        </td>
        {/* Created by */}
        <td className="deemphasize">
          <UserInitials userId={timeReport.createdBy} />
        </td>
        {/* Description */}
        <td className="multiline-inline expand-cell">
          { !_.isEmpty(timeReport.ticket) &&
            <TicketLink ticket={timeReport.ticket} className="emphasize" />
          }
          {timeReport.description}
        </td>
        {/* Edit */}
        <td className="no-padding right-align">
          <AgreementMismatch className="inline-block" item={timeReport} />
          <EditTimeReportPopup className="edit-billable" report={timeReport} afterSubmit={refreshData} />
        </td>
      </Tooltip>
    );
  }
}


export class BillingRow extends React.Component {
  shouldComponentUpdate(nextProps) {
    return (nextProps.billing.checked !== this.props.billing.checked) ||
      (nextProps.billing.updatedAt !== this.props.billing.updatedAt);
  }

  render() {
    const { billing, toggleChecked, refreshData, company } = this.props;
    const disabled = billing.checkboxDisabled;
    const hasUserEscalator = company.hasUserEscalator;
    let netUserChange;
    let changed;
    let increased;
    let escalator;
    let lastMonth;
    let thisMonth;
    let expected;
    let matchesExpected;
    let color = 'text-danger';
    let tooltip;

    if (hasUserEscalator) {
      escalator = company.userEscalator.userEscalatorAmount;
      netUserChange = company.userEscalator.onboardings - company.userEscalator.offboardings;
      changed = netUserChange !== 0;
      increased = netUserChange > 0;
      lastMonth = Number(company.userEscalator.billings[billing.agreement.id]);
      thisMonth = Number(billing.amount);
      
      if (escalator && changed) {
        expected = lastMonth + Number(escalator) * netUserChange;
        matchesExpected = thisMonth === expected;
      } else if (changed) {
        matchesExpected = increased ? thisMonth > lastMonth : thisMonth < lastMonth;
      } else {
        expected = lastMonth;
        matchesExpected = thisMonth === expected;
      }

      if (matchesExpected) color = 'text-warning';
      if (matchesExpected && expected) color = 'text-success';

      tooltip = (
        <table className="text-right">
          <tr>
            <td className="pr-2">Last Month:</td>
            <td className="font-weight-bold">{numeral(lastMonth).format('$0,0.00')}</td>
          </tr>
          <tr>
            <td className="pr-2">User Change:</td>
            <td className="font-weight-bold">
              { increased && '+' }
              {netUserChange}
            </td>
          </tr>
          { escalator &&
            <tr>
              <td className="pr-2">Elevator:</td>
              <td className="font-weight-bold">{numeral(escalator).format('$0,0.00')}</td>
            </tr>
          }
          { expected &&
            <tr>
              <td className="pr-2">Expected:</td>
              <td className="font-weight-bold">{numeral(expected).format('$0,0.00')}</td>
            </tr>
          }
        </table>
      );
    }

    return (
      <Tooltip elementType="tr" title={disabled} className={classNames({ 'disabled-tbody': disabled })} onClick={(e) => toggleChecked(e, billing)}>
        {/* Checkbox */}
        <td className="checkbox-cell">
          { !disabled && <CheckboxInput input={{ value: billing.checked, onChange: _.noop }} /> }
        </td>
        {/* Icon */}
        <td className="icon-cell">
          <Tooltip title="Billing">
            <SVG src="svg/billing.svg" className="billing" />
          </Tooltip>
        </td>
        
        <td />
        <td />

        {/* Amount */}
        <td className="right-align emphasize">
          <div className="d-flex align-items-center justify-content-center">
            <div>{numeral(billing.amount).format('$0,0.00')}</div>
            { hasUserEscalator &&
              <Tooltip className="ml-2" title={tooltip}>
                <SVG src="svg/person.svg" className={color} />
              </Tooltip>
            }
          </div>
        </td>
        {/* Date */}
        <td className="deemphasize">
          {moment(billing.releaseDate).format('M/D')}
        </td>
        {/* Agreement */}
        <td className="deemphasize">
          <AgreementLink agreement={billing.agreement}>
            {billing.agreement.nickname}
          </AgreementLink>
        </td>

        <td />

        {/* Description */}
        <td className="expand-cell">
          {billing.description}
        </td>
        <td className="no-padding right-align">
          <EditBillingPopup className="edit-billable" billing={billing} afterSubmit={refreshData} />
        </td>
      </Tooltip>
    );
  }
}


export class ExpenseBillRows extends React.Component {
  shouldComponentUpdate(nextProps) {
    return (nextProps.expenseBill.checked !== this.props.expenseBill.checked) ||
      (nextProps.expenseBill.checkboxDisabled !== this.props.expenseBill.checkboxDisabled) ||
      (nextProps.expenseBill.updatedAt !== this.props.expenseBill.updatedAt);
  }

  render() {
    const { expenseBill } = this.props;
    const disabled = expenseBill.checkboxDisabled;
    
    return (
      <Tooltip elementType="tbody" title={disabled} className={classNames({ 'disabled-tbody': disabled }, 'grouped-body')}>
        <ExpenseBillRow {...this.props} />
        {_.map(expenseBill.lineItems, (lineItem) =>
          <LineItemRow item={lineItem} key={lineItem.id} {...this.props} />
        )}
        <ShippingTaxTotalRow amount={expenseBill.shipping} description={'Shipping'} {...this.props} />
        <ShippingTaxTotalRow amount={expenseBill.tax} description={'Sales Tax Paid'} {...this.props} />
        <ShippingTaxTotalRow amount={expenseBill.amount} description={'Total'} {...this.props} />
      </Tooltip>
    );
  }
}


export function ExpenseBillRow({ expenseBill, toggleChecked, refreshData }) {
  return (
    <tr onClick={(e) => toggleChecked(e, expenseBill)}>
      {/* Checkbox */}
      <td className="checkbox-cell">
        { !expenseBill.checkboxDisabled &&
          <CheckboxInput input={{ value: expenseBill.checked, onChange: _.noop }} />
        }
      </td>
      {/* Icon */}
      <td className="icon-cell">
        <Tooltip title="Expense Bill">
          <SVG src="svg/expense-bill.svg" className="expense-bill" />
        </Tooltip>
      </td>

      {/* Amount */}
      <td className="right-align" colSpan="3" />
      {/* Date */}
      <td className="deemphasize">
        {moment(expenseBill.date).format('M/D')}
      </td>
      {/* Agreement */}
      <td className="deemphasize">
        {expenseBill.agreement.nickname}
      </td>
      {/* Created by */}
      <td className="deemphasize">
        <UserInitials userId={expenseBill.createdBy} />
      </td>

      <td className="expand-cell">
        <TicketLink ticket={expenseBill.purchaseOrder.ticket} className="emphasize" />
      </td>
      {/* Edit */}
      <td className="no-padding right-align">
        <AgreementMismatch className="inline-block" item={expenseBill} />
        <EditExpenseBillPopup
          className="edit-billable"
          expenseBill={{ ...expenseBill, purchaseOrder: expenseBill.purchaseOrder.id }}
          afterSubmit={refreshData}
        />
      </td>
    </tr>
  );
}


export function LineItemRow({ item, expenseBill, toggleChecked }) {
  return (
    <tr onClick={(e) => toggleChecked(e, expenseBill)} className="line-item-row">
      <td />
      <td />

      {/* Quantity */}
      <td className="right-align deemphasize">
        {item.quantity}
      </td>
      {/* Amount */}
      <td className="right-align deemphasize">
        {numeral(item.unitCost).format('$0,0.00')}
      </td>
      {/* Total */}
      <td className={classNames({ negative: (item.quantity * item.unitCost) < 0 }, 'right-align')}>
        {numeral(item.quantity * item.unitCost).format('$0,0.00')}
      </td>

      <td />
      <td />
      <td />

      {/* Description */}
      <td className="expand-cell">
        {item.purchaseOrderLineItem.description}
      </td>
      <td />
    </tr>
  );
}


export function ShippingTaxTotalRow({ amount, description, expenseBill, toggleChecked }) {
  const emphasis = description === 'Total' ? 'emphasize' : 'deemphasize';

  return (
    <tr className="shipping-tax-total-row line-item-row" onClick={(e) => toggleChecked(e, expenseBill)}>
      <td />
      <td />
      <td />
      <td />

      {/* Total */}
      <td className={classNames('right-align', emphasis, { negative: amount < 0 })}>
        {numeral(amount).format('$0,0.00')}
      </td>

      <td />
      <td />
      <td />

      {/* Description */}
      <td className={classNames({ deemphasize: description !== 'Total' }, 'expand-cell')}>
        {description}
      </td>

      <td />
    </tr>
  );
}
